import { useState } from 'react';
import classnames from 'classnames';

import Analytics, { ElementShownAnchor } from '@hh.ru/analytics-js';
import { Card, Text, VSpacing } from '@hh.ru/magritte-ui';
import { ElementSpyInstance } from 'bloko/common/elementSpy';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Rating, { RatingValue } from 'src/components/TempMagritteRating';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { fetcher } from 'src/utils/fetcher';

import styles from './styles.less';

const SEARCH_RATING = '/notices/mark_as_viewed';

declare global {
    interface FetcherPostApi {
        [SEARCH_RATING]: {
            queryParams: null;
            body: { name: string };
            response: void;
        };
    }
}

const TrlKeys = {
    title: 'vacancy.search.rating.title',
    thnx: 'vacancy.search.rating.thnxMessage',
    smiles: {
        sad: 'vacancy.search.rating.sad',
        unhappy: 'vacancy.search.rating.unhappy',
        indifirent: 'vacancy.search.rating.indifirent',
        smirk: 'vacancy.search.rating.smirk',
        happy: 'vacancy.search.rating.happy',
    },
};

const sendElementShown = (element: HTMLElement): ElementSpyInstance =>
    Analytics.sendHHEventElementShown(element, {
        name: 'select_search_rating_shown',
        portfolio: 'PORTFOLIO-14535',
        requestUrl: window.location.href,
    });

enum Smiles {
    Sad = 'sad',
    Unhappy = 'unhappy',
    Indifirent = 'indifirent',
    Smirk = 'smirk',
    Happy = 'happy',
}

interface SearchRatingProps {
    portfolio: string;
    eventName: string;
    infoTipName: string;
}

const MAP_SMILE_VALUE = {
    [RatingValue.Sad]: Smiles.Sad,
    [RatingValue.Halfsad]: Smiles.Unhappy,
    [RatingValue.Neutral]: Smiles.Indifirent,
    [RatingValue.Halfsmile]: Smiles.Smirk,
    [RatingValue.Smile]: Smiles.Happy,
};

const SearchRating: TranslatedComponent<SearchRatingProps> = ({ trls, portfolio, eventName, infoTipName }) => {
    const searchRating = useSelector((state) => state.searchRating);
    const [clicked, setClicked] = useState<Smiles | null>(null);

    const clickEvent = (value: Smiles) => {
        if (clicked === null) {
            Analytics.sendHHEventButtonClick(eventName, {
                portfolio,
                requestUrl: window.location.href,
                score: value,
            });
            fetcher
                .post(SEARCH_RATING, {
                    name: infoTipName,
                })
                .then(() => {
                    setClicked(value);
                })
                .catch(console.error);
        }
    };

    if (!searchRating) {
        return null;
    }

    return (
        <ElementShownAnchor fn={sendElementShown}>
            <Card borderRadius={16} padding={16} borderWidth="default" stretched data-qa="search-rating">
                <Text typography="title-5-semibold" data-qa="search-rating-title">
                    {trls[TrlKeys.title]}
                </Text>

                <VSpacing default={16} />
                <div className={classnames({ [styles.inactive]: !!clicked })}>
                    {/* TODO: заменить на компонент магритта в https://jira.hh.ru/browse/PORTFOLIO-31847 */}
                    <Rating
                        name="search-rating"
                        onChange={(rating) => {
                            clickEvent(MAP_SMILE_VALUE[rating]);
                        }}
                        dataQaWrapper="search-rating-values"
                    />
                </div>
            </Card>
            <VSpacing default={12} />
        </ElementShownAnchor>
    );
};

export default translation(SearchRating);
